/**
 * Best-effort system-clipboard image reader (zero-dependency).
 *
 * Used by the TUI's Ctrl+V handler: terminals only deliver *text* through
 * bracketed paste, so an image on the clipboard never reaches the input by
 * itself. When the user explicitly asks (Ctrl+V), we shell out to the
 * platform's clipboard utility and pull the image as PNG bytes.
 *
 * Per platform:
 *  - macOS: `osascript` — first the raw PNG flavour («class PNGf», what
 *    screenshots put on the clipboard), then a copied-file fallback
 *    («class furl», what Finder ⌘C puts there) read from disk.
 *  - Linux: `wl-paste` (Wayland) then `xclip` (X11), asking for image/png.
 *  - Windows: PowerShell's Windows.Forms clipboard, saved as PNG.
 *
 * All paths are best-effort: any missing utility or non-image clipboard
 * resolves to null, never throws.
 */
import { execFile } from "node:child_process";
import fs from "node:fs";
import os from "node:os";
import path from "node:path";

export interface ClipboardImage {
  data: string; // base64-encoded image bytes
  mime: string; // e.g. "image/png"
}

const FILE_MIMES: Record<string, string> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".webp": "image/webp",
};

/** Cap pasted images at 8 MB — larger than any reasonable screenshot, small
 *  enough that a base64 body stays well within request limits. */
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

function run(cmd: string, args: string[], maxBuffer = MAX_IMAGE_BYTES * 2): Promise<{ ok: boolean; stdout: Buffer }> {
  return new Promise((resolve) => {
    execFile(cmd, args, { encoding: "buffer", maxBuffer }, (err, stdout) => {
      resolve({ ok: !err, stdout: stdout ?? Buffer.alloc(0) });
    });
  });
}

function fromFile(filePath: string): ClipboardImage | null {
  const mime = FILE_MIMES[path.extname(filePath).toLowerCase()];
  if (!mime) return null;
  try {
    const stat = fs.statSync(filePath);
    if (!stat.isFile() || stat.size === 0 || stat.size > MAX_IMAGE_BYTES) return null;
    return { data: fs.readFileSync(filePath).toString("base64"), mime };
  } catch {
    return null;
  }
}

async function readDarwin(): Promise<ClipboardImage | null> {
  // Raw image data (screenshots, "Copy Image" from a browser) → PNG flavour.
  const tmp = path.join(os.tmpdir(), `sc-clip-${process.pid}-${Date.now()}.png`);
  const script = [
    `set d to the clipboard as «class PNGf»`,
    `set f to open for access POSIX file "${tmp}" with write permission`,
    `set eof f to 0`,
    `write d to f`,
    `close access f`,
  ].join("\n");
  const png = await run("osascript", ["-e", script]);
  if (png.ok) {
    const img = fromFile(tmp);
    try {
      fs.unlinkSync(tmp);
    } catch {
      /* ignore */
    }
    if (img) return img;
  }
  // A file copied in Finder → resolve the file URL and read the image from disk.
  const furl = await run("osascript", ["-e", "POSIX path of (the clipboard as «class furl»)"]);
  if (furl.ok) {
    const p = furl.stdout.toString("utf8").trim();
    if (p) return fromFile(p);
  }
  return null;
}

async function readLinux(): Promise<ClipboardImage | null> {
  for (const [cmd, args] of [
    ["wl-paste", ["--type", "image/png"]],
    ["xclip", ["-selection", "clipboard", "-t", "image/png", "-o"]],
  ] as [string, string[]][]) {
    const res = await run(cmd, args);
    // PNG magic bytes guard against utilities that print an error to stdout.
    if (res.ok && res.stdout.length > 8 && res.stdout.length <= MAX_IMAGE_BYTES && res.stdout[0] === 0x89 && res.stdout[1] === 0x50) {
      return { data: res.stdout.toString("base64"), mime: "image/png" };
    }
  }
  return null;
}

async function readWindows(): Promise<ClipboardImage | null> {
  const tmp = path.join(os.tmpdir(), `sc-clip-${process.pid}-${Date.now()}.png`);
  const ps = [
    "Add-Type -AssemblyName System.Windows.Forms;",
    "$img = [System.Windows.Forms.Clipboard]::GetImage();",
    `if ($img -ne $null) { $img.Save('${tmp.replace(/'/g, "''")}', [System.Drawing.Imaging.ImageFormat]::Png) }`,
  ].join(" ");
  await run("powershell", ["-NoProfile", "-STA", "-Command", ps]);
  const img = fromFile(tmp);
  try {
    fs.unlinkSync(tmp);
  } catch {
    /* ignore */
  }
  return img;
}

/** Read an image from the system clipboard, or null when there isn't one. */
export async function readClipboardImage(): Promise<ClipboardImage | null> {
  try {
    if (process.platform === "darwin") return await readDarwin();
    if (process.platform === "win32") return await readWindows();
    return await readLinux();
  } catch {
    return null;
  }
}
